<template>
    <div>

        <div class="l-element">
            <div class="c-preview-example" v-if="!photoFile" data-jest="form-input-certificate-example">
                <img :src="exampleImageUrl" alt="撮影イメージ"/>
            </div>
            <div class="c-preview-photo" v-show="photoFile" data-jest="form-input-certificate-photo">
                <div @click="isActiveModal = true" class="c-preview-photo-close" :data-jest="`form-input-${formId}-certificate-photo-close`" />
                <canvas :id="`canvas_${formId}`" class="c-preview-photo-canvas"></canvas>
            </div>
        </div>
        <slot></slot>
        <label :for="`input_${formId}`">
            <btn class="l-element c-button-icon" :priority="photoFile ? cssPriority.DISABLED : priority" :text="buttonText" data-jest="form-input-certificate-photo-shoot-btn"/>
            <input v-if="!photoFile" :id="`input_${formId}`" :type="inputPhotoType" accept="image/*"
                   @click="onClickTakePhoto"
                   @change="inputPhoto" class="c-input-photo" capture
                   data-jest="form-input-certificate-photo-shoot-input">
        </label>

        <modal v-if="isActiveModal" @close="isActiveModal= false" ga4-event-label="FORM_CERTIFICATE_DELETE" data-jest="modal-certificate-delete">
            <div class="l-element">
                <heading-plain text="画像の削除" :isCenter="true" />
            </div>
            <div class="l-element">
                <p class="m-text-normal is-mgb0 is-text-center">撮影済みの画像を削除しますか？</p>
                <btn class="l-element" @click="deletePhoto" text="削除する" data-jest="modal-certificate-delete-submit"/>
            </div>
        </modal>

    </div>
</template>

<script>
import Btn from "../atoms/Btn.vue";
import HeadingPlain from "../atoms/HeadingPlain.vue";
import Modal from "../molecules/Modal.vue";
import {cssPriority} from "../../view-model/enum/CSSPriority";

export default {
    name: "FormInputCertificate",
    components: {
        Btn, HeadingPlain, Modal
    },
    props: {
        /**
         * 識別id
         */
        formId: {
            type: String,
            required: false,
            default: ""
        },
        /**
         * 撮影例画像のURL
         */
        exampleImageUrl: {
            type: String,
            required: true
        },
        /**
         * 撮影ボタンのページ内優先度
         */
        priority: {
            type: Symbol,
            required: false,
            default: cssPriority.PRIMARY
        },
        /**
         * 撮影ボタンのテキスト
         */
        buttonText: {
            type: String,
            required: false,
            default: "画像を撮影する"
        },
        /**
         * 初期プレビュー（すでに撮影済みの画像がある場合）
         */
        initialPhotoFile: {
            type: File,
            required: false,
            default: null
        },
        /**
         * 表示時状態か
         * （非表示の場合はメモリ使用量オーバー防止のため、一旦canvasを削除する）
         */
        isDisplay: {
            type: Boolean,
            required: false,
            default: true
        },
        cameraPermitted: {
            type: Boolean,
            required: false,
            default: true
        }
    },
    emits: ['input', 'delete'],
    data() {
        return {
            cssPriority: cssPriority,
            photoFile: null,
            isActiveModal: false,
            ctx: null,
        }
    },
    watch: {
        photoFile() {
            if(this.photoFile){
                this.renderFile()
            }
            this.emit("input", this.photoFile)
        },
        initialPhotoFile() {
            if(this.initialPhotoFile){
                this.photoFile = this.initialPhotoFile
            }
        },
        isDisplay() {
            if(this.isDisplay && this.photoFile){
                this.renderFile()
            } else if (!this.isDisplay && this.ctx) {
                this.deleteCanvas()
            }
        },
    },
    mounted() {
        if(this.initialPhotoFile){
            this.photoFile = this.initialPhotoFile
        }
    },
    beforeUnmount() {
        this.deleteCanvas()
    },
    methods: {
        onClickTakePhoto() {
            const message = {
               formId: this.formId
            };
            window.webkit?.messageHandlers?.openCamera?.postMessage(JSON.stringify(message));
        },
        inputPhoto(file) {
            this.photoFile = file.target.files[0]
        },
        renderFile() {
            const reader = new FileReader();
            const self = this

            reader.onload = function (e) {
                const dataURL = e.target.result
                const c = document.getElementById(`canvas_${self.formId}`);
                self.deleteCanvas()
                self.ctx = c.getContext('2d')
                const img = new Image();

                const scale = 0.5; // 端末のメモリ節約のため、縦横を50%縮小
                img.onload = function () {
                    const dstWidth = img.width * scale;
                    const dstHeight = img.height * scale
                    c.width = dstWidth;
                    c.height = dstHeight;
                    self.ctx.drawImage(img, 0, 0, img.width, img.height, 0, 0, dstWidth, dstHeight);
                };

                img.src = dataURL;
            };
            reader.readAsDataURL(this.photoFile);
        },
        emit(emitName, value) {
            this.$emit(emitName, value);
        },
        deletePhoto() {
            this.photoFile = null
            this.isActiveModal = false
            this.deleteCanvas()
            this.emit("delete", null);
        },
        deleteCanvas() {
            const c = document.getElementById(`canvas_${this.formId}`);
            if(c){
                c.width = 0;
                c.height = 0;
            }
            if(this.ctx){
                delete this.ctx
            }
        },
    },
    computed: {
        inputPhotoType() {
            return this.cameraPermitted ? "file" : "button"
        }
    }
}
</script>

<style scoped lang="scss">
@use "../../../scss/v2/base/variables";
$grid-base: 4.27vw; // 375px幅に対して16px
$grid-consistent: $grid-base * 0.5; // 375px幅に対して8px

.c-preview-example {
    background-color: variables.$color-pale-blue;
    padding: 13px ($grid-consistent * 0.5);
}

.c-button-icon {
    position: relative;

    :deep(.m-btn-primary::before),
    :deep(.m-btn-secondary::before) {
        display: block;
        content: '';
        position: absolute;
        left: 0;
        margin-left: $grid-base * 2;
        width: $grid-consistent * 3;
        height: $grid-consistent * 3;
        background-size: cover;
    }

    :deep(.m-btn-primary::before) {
        background-image: url('/img/jhs_free/icon_camera_white.png');
    }

    :deep(.m-btn-secondary::before) {
        background-image: url('/img/jhs_free/icon_camera_pink.png');
    }
}

.c-preview-photo{
    position: relative;

    &-canvas {
        width: 100%
    }

    &-close {
        display: block;
        position: absolute;
        right: 0%;
        margin-top: $grid-consistent;
        margin-right: $grid-consistent;
        width: $grid-base * 2;
        height: $grid-base * 2;
        border-radius: 50%;  /* 丸みの度合い */
        background: rgba(variables.$color-black, variables.$opacity-50); /* ボタンの背景色 */

        &::before, &::after {
            content: "";
            position: absolute;
            top: 50%;
            left: 50%;
            width: 1px; /* 棒の幅（太さ） */
            height: 13px; /* 棒の高さ */
            background: variables.$color-white; /* バツ印の色 */
        }

        &::before {
            transform: translate(-50%,-50%) rotate(45deg);
        }

        &::after {
            transform: translate(-50%,-50%) rotate(-45deg);
        }
    }
}

.c-input-photo {
    display: none
}

</style>
